import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Instagram, Facebook, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';

const WHATSAPP_NUMBER = "351913918967";

export const Contact = () => {
  const openWhatsApp = () => {
    const message = encodeURIComponent("Olá Pet Guardiões! Gostaria de obter mais informações.");
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const url = isMobile 
      ? `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`
      : `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${message}`;
    window.open(url, '_blank');
  };

  return (
    <section 
      id="contactos" 
      className="py-20 md:py-28 bg-gradient-to-b from-white to-yellow-50 relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background decoration */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 text-yellow-600 font-semibold mb-4">
              <MapPin className="w-4 h-4" />
              Contactos
            </span>
            <h2 className="font-nunito text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6">
              Visite-nos em<br />
              <span className="text-yellow-500">Setúbal</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Estamos prontos para receber o seu patudo com todo o carinho e profissionalismo.
            </p>

            <div className="space-y-6 mb-10">
              {/* Address */}
              <motion.div 
                className="flex items-start gap-4"
                whileHover={{ x: 5 }}
              >
                <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Morada</h4>
                  <p className="text-gray-600">Rua Dom Pedro Fernandes Sardinha, 66</p>
                  <p className="text-gray-600">2910-633 Setúbal, Portugal</p>
                </div>
              </motion.div>

              {/* Phone */}
              <motion.div 
                className="flex items-start gap-4"
                whileHover={{ x: 5 }}
              >
                <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">WhatsApp</h4>
                  <a 
                    href={`tel:+${WHATSAPP_NUMBER}`}
                    className="text-gray-600 hover:text-yellow-600 transition-colors"
                  >
                    +351 913 918 967
                  </a>
                </div>
              </motion.div>

              {/* Hours */}
              <motion.div 
                className="flex items-start gap-4"
                whileHover={{ x: 5 }}
              >
                <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Horário</h4>
                  <p className="text-gray-600">Terça a Sábado: 10h00 - 18h00</p>
                  <p className="text-gray-500 text-sm">Segunda-feira: encerrado</p>
                </div>
              </motion.div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 mb-10">
              <motion.a
                href="https://www.instagram.com/petguardioes.setubal/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 bg-white rounded-2xl shadow-lg shadow-black/5 flex items-center justify-center hover:bg-yellow-100 transition-colors group"
                whileHover={{ scale: 1.1, rotate: 5 }}
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram className="w-6 h-6 text-gray-700 group-hover:text-yellow-600" />
              </motion.a>
              <motion.a
                href="https://www.facebook.com/petguardioes.setubal/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 bg-white rounded-2xl shadow-lg shadow-black/5 flex items-center justify-center hover:bg-yellow-100 transition-colors group"
                whileHover={{ scale: 1.1, rotate: -5 }}
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook className="w-6 h-6 text-gray-700 group-hover:text-yellow-600" />
              </motion.a>
            </div>

            {/* CTA */}
            <Button
              onClick={openWhatsApp}
              className="bg-green-500 hover:bg-green-400 text-white font-bold text-lg px-8 py-6 rounded-full shadow-xl shadow-green-500/30 hover:shadow-green-500/50 transition-all"
              data-testid="contact-whatsapp-btn"
            >
              <Phone className="w-5 h-5 mr-2" />
              Contactar pelo WhatsApp
            </Button>
          </motion.div>

          {/* Right - Map/Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            {/* Decorative elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-yellow-400 rounded-3xl -z-10 rotate-12" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-yellow-200 rounded-3xl -z-10 -rotate-12" />

            {/* Map embed */}
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-yellow-500/20 border-4 border-white">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3113.1234567890123!2d-8.8878!3d38.5244!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd1943a8c8888889%3A0x888888888888888!2sRua%20Dom%20Pedro%20Fernandes%20Sardinha%2066%2C%20Set%C3%BAbal!5e0!3m2!1spt-PT!2spt!4v1234567890123"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Pet Guardiões"
                className="w-full"
              />
            </div>

            {/* Info card overlay */}
            <motion.div
              className="absolute -bottom-6 left-6 right-6 bg-white rounded-2xl p-4 shadow-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white fill-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Aqui, o seu patudo é tratado como família</p>
                  <p className="text-sm text-gray-500">Pet Guardiões – Banho & Tosquia</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
